import videojs from 'video.js';
import Player from 'video.js/dist/types/player';
import { action, actionOn, thunk, Thunk, Action, ActionOn, ThunkOn, thunkOn, State } from 'easy-peasy';
import { StoreState } from './store';
import { PlayerError } from '../errors/PlayerError';
import { ErrorCodes } from '../errors/ErrorCodes';
import { requestFullscreen, exitFullscreen } from '../utils/fullscreen';
import { keyCodes } from '../utils/key-codes';
import { getAudioTracks, getTextTracks, getDefaultAudioTrack } from '../utils/tracks';
import { PlayerStatus, VideoType, VideoItem } from '../typings';
import { throttle } from 'lodash';
import { ChromecastUtils } from '../utils/chromecast';
import { getVideoFrame } from '../utils/get-video-frame';
import { Browser } from '../utils/browser';

export interface PlayerState {
  playerElement: HTMLDivElement | null;
  videoElement: HTMLVideoElement | null;
  videoFrame: string | null;
  videoEngine: Player | null;
  videoItem: VideoItem | null;
  playerReady: boolean;
  status: PlayerStatus;
  playbackDurationTime: number;
  playbackCurrentTime: number;
  playbackBufferingTime: number;
  playbackSwitchTime: number;
  playbackResumePoint: number;
  playbackStalled: boolean;
  playbackStalledTimeout: number | null;
  playbackLoadProgressTime: Date | null;
  framerate: number;
  bitrate: number;
  muted: boolean;
  volume: number;
  ignoreVideoEvents: boolean;
  audioTracks: AudioTrack[];
  audioTrack: AudioTrack | null;
  controlsVisible: boolean;
  languageMenuVisible: boolean;
  subtitleOptionsVisible: boolean;
  resumeOverlayVisible: boolean;
  contextMenuVisible: boolean;
  contextMenuPosition: { x: number; y: number } | null;

  setVideoElement: Action<PlayerState, HTMLVideoElement>;
  setVideoItem: Action<PlayerState, VideoItem | null>;
  setVideoFrame: Action<PlayerState, PlayerState['videoFrame']>;
  setPlayerElement: Action<PlayerState, HTMLDivElement>;
  setPlayerReady: Action<PlayerState, boolean>;
  setFramerate: Action<PlayerState, number>;
  setBitrate: Action<PlayerState, number>;
  setVolume: Action<PlayerState, number>;
  setMuted: Action<PlayerState, boolean>;
  setStatus: Action<PlayerState, PlayerStatus>;
  setIgnoreVideoEvents: Action<PlayerState, boolean>;
  setPlaybackDurationTime: Action<PlayerState, number>;
  setPlaybackCurrentTime: Action<PlayerState, number>;
  setPlaybackBufferingTime: Action<PlayerState, number>;
  setPlaybackSwitchTime: Action<PlayerState, number>;
  setPlaybackResumePoint: Action<PlayerState, number>;
  setPlaybackLoadProgressTime: Action<PlayerState, PlayerState['playbackLoadProgressTime']>;
  setAudioTracks: Action<PlayerState, AudioTrack[]>;
  setAudioTrack: Thunk<PlayerState, AudioTrack | null, any, StoreState>;
  setPlayerControlsVisible: Action<PlayerState, boolean>;
  setLanguageMenuVisible: Action<PlayerState, boolean>;
  setSubtitleOptionsVisible: Action<PlayerState, boolean>;
  setResumeOverlayVisible: Action<PlayerState, boolean>;
  setContextMenuVisible: Action<PlayerState, boolean>;
  setContextMenuPosition: Action<PlayerState, { x: number; y: number } | null>;

  play: Action<PlayerState>;
  pause: Action<PlayerState>;
  seek: Action<PlayerState, number>;
  rewind: Action<PlayerState>;
  fastForward: Action<PlayerState>;
  mute: Action<PlayerState>;
  unmute: Action<PlayerState>;

  loadVideo: Thunk<PlayerState, VideoItem, any, StoreState>;
  loadAudioTracks: Thunk<PlayerState, AudioTrack[], any, StoreState>;
  enableFullScreen: Thunk<PlayerState>;
  disableFullScreen: Thunk<PlayerState>;
  processKeyPress: Thunk<PlayerState, KeyboardEvent, any, StoreState>;
  checkBuffering: Thunk<PlayerState, void, any, StoreState>;
  checkFramerateAndBitrate: Thunk<PlayerState, void, any, StoreState>;
  checkAudioTrackSelected: Thunk<PlayerState, void, any, StoreState>;
  reset: Action<PlayerState, void>;
  destroy: Thunk<PlayerState, void>;

  handleVideoLoadedData: Thunk<PlayerState, void, any, StoreState>;
  handleVideoPlaying: Thunk<PlayerState, void, any, StoreState>;
  handleVideoPause: Thunk<PlayerState, void, any, StoreState>;
  handleVideoEnded: Thunk<PlayerState, void, any, StoreState>;
  handleVideoWaiting: Thunk<PlayerState, void, any, StoreState>;
  handleVideoTimeUpdate: Thunk<PlayerState, void, any, StoreState>;
  handleVideoProgress: Thunk<PlayerState, void, any, StoreState>;
  handleVideoError: Thunk<PlayerState, void, any, StoreState>;
  shouldHandleVideoEvent: Thunk<PlayerState, void, any, StoreState>;

  onPlayerDataFetched: ThunkOn<PlayerState, void, StoreState>;
  onPlayerVideoLoaded: ThunkOn<PlayerState>;
  onPlayerReady: ThunkOn<PlayerState, void, StoreState>;
  onPlayerDestroy: ThunkOn<PlayerState, void, StoreState>;

  onPlayerPlay: ThunkOn<PlayerState, void, StoreState>;
  onPlayerPause: ThunkOn<PlayerState, void, StoreState>;
  onPlayerSeek: ThunkOn<PlayerState, void, StoreState>;
  onPlayerProgress: ThunkOn<PlayerState, number, StoreState>;
  onPlayerVolumeChanged: ThunkOn<PlayerState, number, StoreState>;
  onPlayerControlsVisibileChange: ActionOn<PlayerState>;
  onPlayerControlPressed: ActionOn<PlayerState>;
}

export const initialState: State<PlayerState> = {
  videoElement: null,
  videoFrame: null,
  videoEngine: null,
  videoItem: null,
  playerElement: null,
  playerReady: false,
  status: PlayerStatus.Uninitialized,
  playbackDurationTime: 0,
  playbackCurrentTime: 0,
  playbackBufferingTime: -1,
  playbackSwitchTime: 0,
  playbackResumePoint: 0,
  playbackStalled: false,
  playbackStalledTimeout: null,
  playbackLoadProgressTime: null,
  framerate: 0, // FPS
  bitrate: 0, // Kbps
  muted: false,
  volume: 100,
  ignoreVideoEvents: false,
  audioTracks: [],
  audioTrack: null,
  controlsVisible: true,
  languageMenuVisible: false,
  subtitleOptionsVisible: false,
  resumeOverlayVisible: false,
  contextMenuVisible: false,
  contextMenuPosition: null,
};

export const playerState: PlayerState = {
  /*================
   * State      
   ================*/
  ...initialState,

  /*================
   * State Actions      
   ================*/
  setVideoElement: action((state, videoElement) => {
    state.videoElement = videoElement;
  }),

  setVideoFrame: action((state, videoFrameOverlay) => {
    state.videoFrame = videoFrameOverlay;
  }),

  setVideoItem: action((state, videoItem) => {
    state.videoItem = videoItem;
  }),

  setPlayerElement: action((state, playerElement) => {
    state.playerElement = playerElement;
  }),

  setPlayerReady: action((state, ready) => {
    state.playerReady = ready;
  }),

  setFramerate: action((state, framerate) => {
    state.framerate = framerate;
  }),

  setBitrate: action((state, bitrate) => {
    state.bitrate = bitrate;
  }),

  setVolume: action((state, volume) => {
    if (volume > 100) volume = 100;
    if (volume < 0) volume = 0;

    if (state.videoEngine) {
      state.videoEngine.volume(volume / 100);
      state.volume = volume;
    }
  }),

  setMuted: action((state, muted) => {
    state.muted = muted;
  }),

  setStatus: action((state, status) => {
    state.status = status;
  }),

  setIgnoreVideoEvents: action((state, shouldIgnoreVideoEvents) => {
    state.ignoreVideoEvents = shouldIgnoreVideoEvents;
  }),

  setPlaybackDurationTime: action((state, durationTime) => {
    state.playbackDurationTime = durationTime;
  }),

  setPlaybackCurrentTime: action((state, currentTime) => {
    state.playbackCurrentTime = currentTime;
  }),

  setPlaybackBufferingTime: action((state, bufferingTime) => {
    state.playbackBufferingTime = bufferingTime;
  }),

  setPlaybackSwitchTime: action((state, switchTime) => {
    state.playbackSwitchTime = switchTime;
  }),

  setPlaybackResumePoint: action((state, resumeTime) => {
    state.playbackResumePoint = resumeTime;
  }),

  setPlaybackLoadProgressTime: action((state, playbackLoadProgressTime) => {
    state.playbackLoadProgressTime = playbackLoadProgressTime;
  }),

  setAudioTracks: action((state, audioTracks) => {
    const audioTracksSorted = audioTracks.sort((trackA, trackB) => {
      if (Browser.isSafari || Browser.isIOS) return trackA.id > trackB.id ? 1 : -1;
      return 0;
    });

    state.audioTracks = audioTracksSorted;
  }),

  setAudioTrack: thunk((_, audioTrack, { getState }) => {
    if (audioTrack) {
      const state = getState();
      state.audioTrack = audioTrack;
      state.audioTracks.forEach((track) => (track.enabled = track === audioTrack));
    }
  }),

  setPlayerControlsVisible: action((state, visible) => {
    state.controlsVisible = visible;
  }),

  setLanguageMenuVisible: action((state, visible) => {
    state.languageMenuVisible = visible;
  }),

  setSubtitleOptionsVisible: action((state, visible) => {
    state.subtitleOptionsVisible = visible;
  }),

  setResumeOverlayVisible: action((state, visible) => {
    state.resumeOverlayVisible = visible;
  }),

  setContextMenuVisible: action((state, visible) => {
    state.contextMenuVisible = visible;
  }),

  setContextMenuPosition: action((state, position) => {
    state.contextMenuPosition = position;
  }),

  play: action((state) => {
    if (state.videoEngine) {
      state.videoEngine.play();
    }
  }),

  pause: action((state) => {
    if (state.videoEngine) {
      state.videoEngine.pause();
    }
  }),

  seek: action((state, targetTime) => {
    if (state.videoEngine) {
      state.videoEngine.currentTime(targetTime);
      state.playbackCurrentTime = targetTime;
    }
  }),

  rewind: action((state) => {
    if (state.videoEngine) {
      const segment = 15;
      const currentTime = Math.max(0, state.playbackCurrentTime - segment);

      state.videoEngine.currentTime(currentTime);
      state.playbackCurrentTime = currentTime;
    }
  }),

  fastForward: action((state) => {
    if (state.videoEngine) {
      const segment = 15;
      const duration = state.videoEngine.duration();

      if (duration) {
        const currentTime = Math.min(duration - 1, state.playbackCurrentTime + segment);

        state.videoEngine.currentTime(currentTime);
        state.playbackCurrentTime = currentTime;
      }
    }
  }),

  mute: action((state) => {
    if (state.videoEngine) {
      state.videoEngine.muted(true);
      state.muted = true;
    }
  }),

  unmute: action((state) => {
    if (state.videoEngine) {
      state.videoEngine.muted(false);
      state.muted = false;
    }
  }),

  /**
   * This action configures the video engine (Video.js). Includes
   * event handlers for HLS manifests and captions loading.
   */
  loadVideo: thunk((actions, video, { getState, getStoreActions, getStoreState }) => {
    const state = getState();
    const storeState = getStoreState();
    const overrideNative = !videojs.browser.IS_ANY_SAFARI && !videojs.browser.IS_IOS;

    state.videoItem = video;
    state.status = PlayerStatus.Initialized;
    state.playbackCurrentTime = 0;

    /**
     * Remove the built-in MediaSource.handle property, introduced in Chrome v105,
     * that can be accessed only from a Worker thread but Video.js now is accessing
     * it in the main thread via a property iterator in its source. **/
    // @ts-ignore - MediaSource.handle is not a documented property.
    delete window.MediaSource?.prototype?.handle;

    if (!state.videoEngine) {
      state.videoEngine = videojs('bod-video-player', {
        controls: false,
        controlBar: false,
        html5: {
          nativeVideoTracks: !overrideNative,
          nativeAudioTracks: !overrideNative,
          vhs: {
            overrideNative,
            enableLowInitialPlaylist: false,
          },
        },
      });
    }

    state.videoEngine.src({
      src: video.url,
      type: 'application/x-mpegURL',
    });

    /**
     * Remove all listeners to prevent duplicates
     * when loading new videos in the same player instance.
     */
    state.videoEngine.off([
      'loadeddata',
      'durationchange',
      'playing',
      'pause',
      'ended',
      'waiting',
      'timeupdate',
      'error',
    ]);

    state.videoEngine.on(['loadeddata', 'durationchange'], actions.handleVideoLoadedData);
    state.videoEngine.on('playing', actions.handleVideoPlaying);
    state.videoEngine.on('pause', actions.handleVideoPause);
    state.videoEngine.on('ended', actions.handleVideoEnded);
    state.videoEngine.on('waiting', actions.handleVideoWaiting);
    state.videoEngine.on('timeupdate', throttle(actions.handleVideoTimeUpdate, 1000));
    state.videoEngine.on('progress', actions.handleVideoProgress);
    state.videoEngine.on('error', actions.handleVideoError);

    if (state.videoEngine && videojs.browser.IS_EDGE && storeState.config.maxBitrateForEdge != null) {
      let qualityLevels = state.videoEngine.qualityLevels(null);
      qualityLevels.on('addqualitylevel', function (event: { qualityLevel: { enabled: boolean; bitrate: number } }) {
        let qualityLevel = event.qualityLevel;
        let maxBitrateForEdge = storeState.config.maxBitrateForEdge;
        qualityLevel.enabled = maxBitrateForEdge ? qualityLevel.bitrate < maxBitrateForEdge : true;
      });
    }

    actions.setVideoItem(video);
  }),

  loadAudioTracks: thunk((actions, audioTracks, { getStoreState }) => {
    const storeState = getStoreState();

    actions.setAudioTracks(audioTracks);

    const defaultAudioTrack = getDefaultAudioTrack(audioTracks, storeState);

    if (defaultAudioTrack) {
      actions.setAudioTrack(defaultAudioTrack);
    }
  }),

  enableFullScreen: thunk((_, __, { getState }) => {
    const state = getState();
    if (!state.playerElement) return;
    requestFullscreen(state.playerElement);
  }),

  disableFullScreen: thunk(() => {
    exitFullscreen();
  }),

  processKeyPress: thunk((actions, keyboardEvent, { getState, getStoreState, getStoreActions }) => {
    const state = getState();
    const storeState = getStoreState();
    const storeActions = getStoreActions();

    if (!state.playerReady) return;

    function canControlPlayback(kind: 'playOrPause' | 'fastForward' | 'rewind') {
      let result = true;

      if (state.status === PlayerStatus.Buffering) {
        result = false;
      } else if (state.resumeOverlayVisible) {
        result = false;
      }

      return result;
    }

    switch (keyboardEvent.which) {
      case keyCodes.space:
        if (canControlPlayback('playOrPause')) {
          if (storeState.chromecast.castSession) {
            storeActions.chromecast.playOrPause();
          } else if (state.status === PlayerStatus.Playing) {
            actions.pause();
          } else if (state.status === PlayerStatus.Paused) {
            actions.play();
          }
        }
        break;
      case keyCodes.right:
        if (canControlPlayback('fastForward')) {
          actions.fastForward();
        }
        break;
      case keyCodes.left:
        if (canControlPlayback('rewind')) {
          actions.rewind();
        }
        break;
      case keyCodes.up:
        actions.setVolume(state.volume + 10);
        break;
      case keyCodes.down:
        actions.setVolume(state.volume - 10);
        break;
    }
  }),

  checkBuffering: thunk((actions, event, { getStoreState }) => {
    const storeState = getStoreState();
    if (!storeState.player.playerReady || ChromecastUtils.currentVideoPlaysRemotely(storeState)) return;

    if (
      storeState.player.status === PlayerStatus.Buffering &&
      storeState.player.playbackCurrentTime.toFixed(2) !== storeState.player.playbackBufferingTime.toFixed(2)
    ) {
      actions.setPlaybackBufferingTime(-1);
      actions.setStatus(PlayerStatus.Playing);
    }
  }),

  // NOTE: This is not supported in Safari browsers.
  checkFramerateAndBitrate: thunk((actions, event, { getState }) => {
    const state = getState();
    if (!state.playerReady) return;

    const playerTech = state.videoEngine!.tech({
      IWillNotUseThisInPlugins: true,
    });

    // @ts-ignore
    const hlsTech: any = playerTech.hls;

    if (!hlsTech) return;

    const attr = hlsTech.playlists.media().attributes;
    const bitrate = attr['BANDWIDTH'] / 1000;
    const framerate = parseFloat(attr['FRAME-RATE']);

    if (state.bitrate !== bitrate) {
      actions.setBitrate(bitrate);
    }

    if (state.framerate !== framerate) {
      actions.setFramerate(framerate);
    }
  }),

  /**
   * Ensures the selected audio track in state actually
   * is the enabled one in the videoElement.
   **/
  checkAudioTrackSelected: thunk((actions, _, { getState }) => {
    const state = getState();

    if (!state.videoEngine || !state.audioTrack) return;

    const audioTracks = getAudioTracks(state.videoEngine);
    const selectedAudioTrack = audioTracks.find((audioTrack) => audioTrack.label === state.audioTrack?.label);

    if (selectedAudioTrack?.enabled === false) {
      selectedAudioTrack.enabled = true;
    }
  }),

  /**
   * Reinitializes the store with the initial state.
   * Used when the player is unmounting.
   */
  reset: action((state) => {
    if (state.videoEngine) state.videoEngine.dispose();
    Object.assign(state, { ...initialState });
  }),

  /**
   * This action is excuted when the player is unmounting from the
   * host application. It performs any reset needed, other stores
   * have a listener for this actions and reset themselves.
   */
  destroy: thunk(() => {}),

  /*==============================*
   * Video element listeners      *
   *==============================*/

  handleVideoLoadedData: thunk((actions, event, { getState, getStoreState, getStoreActions }) => {
    const state = getState();
    const storeState = getStoreState();
    const storeActions = getStoreActions();
    const duration = state.videoEngine!.duration();
    const audioTracks = getAudioTracks(state.videoEngine);
    const textTracks = getTextTracks(state.videoEngine);
    const isVideoMetadataParsed = duration && audioTracks.length;

    storeActions.player.setPlaybackDurationTime(duration || 0);

    if (state.audioTracks.length === 0) {
      storeActions.player.loadAudioTracks(audioTracks);
    }

    if (storeState.subtitles.webVttSubtitles.length === 0) {
      storeActions.subtitles.loadSubtitlesFromVideojs(textTracks);
    }

    if (!state.playerReady && isVideoMetadataParsed) {
      if (videojs.browser.IS_ANY_SAFARI || videojs.browser.IS_IOS) state.videoEngine!.pause();
      storeActions.player.setPlayerReady(true);
    }
  }),

  handleVideoPlaying: thunk((actions, event, { getStoreState, getStoreActions }) => {
    if (actions.shouldHandleVideoEvent()) {
      actions.setStatus(PlayerStatus.Playing);
    }
  }),

  handleVideoPause: thunk((actions, event, { getStoreState, getStoreActions }) => {
    const storeState = getStoreState();

    if (storeState.player.status === PlayerStatus.Paused) return;
    if (!storeState.player.playerReady) return;
    if (ChromecastUtils.currentVideoPlaysRemotely(storeState)) return;

    // Skip when video ended. Handle this with the video 'ended' event handler.
    const currentTime = Math.round(storeState.player.videoEngine!.currentTime() || 0);
    const duration = Math.round(storeState.player.videoEngine!.duration() || 0);
    if (currentTime === duration) return;

    actions.setStatus(PlayerStatus.Paused);
  }),

  handleVideoEnded: thunk((actions, event, { getStoreState, getStoreActions }) => {
    const storeState = getStoreState();
    const storeActions = getStoreActions();

    if (!storeState.player.playerReady || ChromecastUtils.currentVideoPlaysRemotely(storeState)) return;

    actions.setStatus(PlayerStatus.Ended);
    storeActions.videos.closePlayer();
  }),

  handleVideoWaiting: thunk((actions, event, { getStoreState, getStoreActions }) => {
    const storeState = getStoreState();

    if (actions.shouldHandleVideoEvent()) {
      actions.setPlaybackBufferingTime(storeState.player.playbackCurrentTime);
      actions.setStatus(PlayerStatus.Buffering);
    }
  }),

  handleVideoTimeUpdate: thunk((actions, event, { getStoreState, getStoreActions }) => {
    const storeState = getStoreState();

    if (actions.shouldHandleVideoEvent()) {
      actions.setPlaybackCurrentTime(storeState.player.videoEngine!.currentTime() || 0);
      actions.checkBuffering();
      actions.checkFramerateAndBitrate();
      actions.checkAudioTrackSelected();
    }
  }),

  handleVideoProgress: thunk((actions, event, { getStoreState, getStoreActions }) => {
    if (actions.shouldHandleVideoEvent()) {
      actions.setPlaybackLoadProgressTime(new Date());
    }
  }),

  handleVideoError: thunk((actions, event, { getStoreState, getStoreActions }) => {
    const storeState = getStoreState();
    const storeActions = getStoreActions();

    const error = storeState.player.videoEngine?.error();

    if (error) {
      let playerError;

      switch (error.code) {
        case 1:
          playerError = new PlayerError(ErrorCodes.MEDIA_ERR_ABORTED, error.message);
          break;
        case 2:
          playerError = new PlayerError(ErrorCodes.MEDIA_ERR_NETWORK, error.message);
          break;
        case 3:
          playerError = new PlayerError(ErrorCodes.MEDIA_ERR_DECODE, error.message);
          break;
        case 4:
          playerError = new PlayerError(ErrorCodes.MEDIA_ERR_SRC_NOT_SUPPORTED, error.message);
          break;
        case 5:
          playerError = new PlayerError(ErrorCodes.MEDIA_ERR_SRC_NOT_SUPPORTED, error.message);
          break;
        default:
          playerError = new PlayerError(error.code || ErrorCodes.DEFAULT, error.message);
      }

      storeActions.error.setError(playerError);
    }
  }),

  shouldHandleVideoEvent: thunk((actions, event, { getStoreState }) => {
    const storeState = getStoreState();

    return (
      storeState.player.playerReady &&
      !storeState.player.ignoreVideoEvents &&
      !ChromecastUtils.currentVideoPlaysRemotely(storeState)
    );
  }),

  /*==============================*
   * Player lifecycle listeners   *
   *==============================*/

  /**
   * When video data is fetched or re-fetched,
   * refresh the current videoItem data */
  onPlayerDataFetched: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.videos.fetchData];
    },
    async function handler(actions, _, { getStoreState }) {
      const { player, videos } = getStoreState();

      if (!player.videoItem) return;

      switch (player.videoItem.type) {
        case VideoType.WORKOUT:
          actions.setVideoItem(videos.workoutVideo);
          break;
        case VideoType.MODIFIER:
          actions.setVideoItem(videos.modifierVideo);
          break;
      }
    }
  ),

  /**
   * When the player video item is set or changed,
   * reset video-specific state values.
   */
  onPlayerVideoLoaded: thunkOn(
    function observer(actions) {
      return [actions.loadVideo.startType];
    },
    function handler(actions) {
      actions.setPlayerReady(false);
      actions.setBitrate(initialState.bitrate);
      actions.setFramerate(initialState.framerate);

      // Reset audio tracks when the video is loaded/changed
      actions.setAudioTracks(initialState.audioTracks);
      actions.setAudioTrack(initialState.audioTrack);
    }
  ),

  /**
   * Ensures the player is completely setup to
   * enable the user experience.
   */
  onPlayerReady: thunkOn(
    function observer(actions) {
      return [actions.setPlayerReady];
    },
    function handler(actions, _, { getState, getStoreState, getStoreActions }) {
      const state = getState();
      const { config, chromecast } = getStoreState();
      const storeActions = getStoreActions();

      if (!state.videoItem || !state.playerReady || !state.playbackDurationTime) return;

      // When video is switching, resume to the switch time
      if (state.playbackSwitchTime) {
        state.videoEngine!.one('canplay', () => {
          state.videoEngine!.currentTime(state.playbackSwitchTime);
          actions.setPlaybackSwitchTime(0);
          if (chromecast.castSession) return; // Skip local playback when casting
          state.videoEngine!.play();
        });

        return;
      }

      // When resume point detected, show resume overlay
      if (state.videoItem.resumePoint && !config.skipResume) {
        // Don't show resume prompt if the video has been watched 75% or more.
        const resumePointIsValid = state.videoItem.resumePoint < state.playbackDurationTime * 0.75;

        if (resumePointIsValid) {
          actions.pause();
          actions.setPlaybackResumePoint(state.videoItem.resumePoint);
          actions.setResumeOverlayVisible(true);
          return;
        }
      }

      // If cast session active, start the playback on chromecast instead of locally
      if (chromecast.castSession) {
        if (state.status !== PlayerStatus.Buffering) {
          storeActions.chromecast.loadMedia();
        }
        return;
      }

      if (config.autoplay) {
        actions.play();
      }
    }
  ),

  onPlayerDestroy: thunkOn(
    function observer(actions) {
      return [actions.destroy];
    },
    function handler(actions) {
      actions.reset();
    }
  ),

  /*==============================*
   * Player interaction listeners *
   *==============================*/

  onPlayerPlay: thunkOn(
    function observer(actions) {
      return [actions.play];
    },
    function handler(_, __, { getStoreState }) {
      const storeState = getStoreState();

      if (storeState.config.onPlayCallback) {
        storeState.config.onPlayCallback();
      }
    }
  ),

  onPlayerPause: thunkOn(
    function observer(actions) {
      return [actions.pause];
    },
    function handler(_, __, { getStoreState }) {
      const storeState = getStoreState();

      if (storeState.config.onPauseCallback) {
        storeState.config.onPauseCallback();
      }
    }
  ),

  onPlayerSeek: thunkOn(
    function observer(actions) {
      return [actions.seek, actions.rewind, actions.fastForward];
    },
    function handler(actions, __, { getStoreState }) {
      const storeState = getStoreState();
      const hasHLSSupport = videojs.browser.IS_ANY_SAFARI || videojs.browser.IS_IOS;

      if (!hasHLSSupport) {
        actions.setVideoFrame(getVideoFrame(storeState.player.videoElement!));
      }

      if (storeState.config.onSeekCallback) {
        storeState.config.onSeekCallback(storeState.player.playbackCurrentTime);
      }
    }
  ),

  onPlayerProgress: thunkOn(
    function observer(actions) {
      return [actions.setPlaybackCurrentTime];
    },
    function handler(actions, __, { getStoreState, getStoreActions }) {
      const storeState = getStoreState();
      const storeActions = getStoreActions();

      storeActions.subtitles.syncSubtitleTimings();

      if (storeState.config.onProgressCallback) {
        storeState.config.onProgressCallback(storeState.player.playbackCurrentTime);
      }
    }
  ),

  /**
   * Mute the player based on volume change.
   **/
  onPlayerVolumeChanged: thunkOn(
    function observer(actions) {
      return [actions.setVolume];
    },
    function handler(_, event, { getStoreState, getStoreActions }) {
      const storeState = getStoreState();
      const storeActions = getStoreActions();

      let shouldMute = storeState.player.volume === 0;

      if (shouldMute !== storeState.player.muted) {
        storeState.player.videoEngine?.muted(shouldMute);
        storeActions.player.setMuted(shouldMute);
      }

      // Save the volume setting locally in unit interval
      storeActions.settings.setVolumeLevel(event.payload / 100);
    }
  ),

  onPlayerControlsVisibileChange: actionOn(
    function observer(actions) {
      return [actions.setPlayerControlsVisible];
    },
    function handler(state, event) {
      if (event.payload === false) {
        state.languageMenuVisible = false;
        state.subtitleOptionsVisible = false;
      }
    }
  ),

  onPlayerControlPressed: actionOn(
    function observer(actions) {
      return [actions.play, actions.pause, actions.fastForward, actions.rewind, actions.seek];
    },
    function handler(state) {
      state.languageMenuVisible = false;
      state.subtitleOptionsVisible = false;
    }
  ),
};
